// Generated by TaroBase SDK Generator: 2026-08-23T03:59:35.512Z
// Collection: subscriptions/$subscriberAddress/creators/$creatorAddress
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Tracks which users (subscribers) follow which creators. Public read so follower/following counts can be displayed. Subscriber owns their subscription record and is the only one who can create it. Subscriptions are immutable once created (toggle off = delete). Deletable by the subscriber OR by PROJECT_VAULT_ADDRESS (backend) — the backend needs to clean up both directions during creator-profile teardown: rows where the deleted user is the subscriber AND rows where they are the creator.
 */

export interface SubscriptionsCreatorsRequest {
  subscriber: AddressType;
  creator: AddressType;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface SubscriptionsCreatorsResponse {
  subscriber: string;
  creator: string;
  createdAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a SubscriptionsCreators operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildSubscriptionsCreators(subscriberAddress: string, creatorAddress: string, data?: SubscriptionsCreatorsRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `subscriptions/${subscriberAddress}/creators/${creatorAddress}`, document: doc };
}

/**
 * Authenticated subscriber only. Caller must equal $subscriberAddress path segment AND @newData.subscriber. The @newData.creator must match the $creatorAddress path segment. Self-subscription is rejected (@newData.subscriber != @newData.creator). All fields (subscriber, creator, createdAt) are immutable after create. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setSubscriptionsCreators(subscriberAddress: string, creatorAddress: string, data?: SubscriptionsCreatorsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`subscriptions/${subscriberAddress}/creators/${creatorAddress}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting SubscriptionsCreators: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Public. Anyone can read subscription records to render follower counts, following lists, and check subscription state.
   (Get Single Item)
 */
export async function getSubscriptionsCreators(subscriberAddress: string, creatorAddress: string): Promise<SubscriptionsCreatorsResponse | null> {
  try {
    const result = await get(`subscriptions/${subscriberAddress}/creators/${creatorAddress}`);
    return result as SubscriptionsCreatorsResponse | null;
  } catch (error) {
    console.error(`Error getting SubscriptionsCreators: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single SubscriptionsCreators document. (
  Read Operation Details: Public. Anyone can read subscription records to render follower counts, following lists, and check subscription state.
  )
 */
export function subscribeSubscriptionsCreators(callback: (data: SubscriptionsCreatorsResponse | null) => void, subscriberAddress: string, creatorAddress: string): Promise<() => Promise<void>> {
  return subscribe(`subscriptions/${subscriberAddress}/creators/${creatorAddress}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as SubscriptionsCreatorsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to SubscriptionsCreators at path '${`subscriptions/${subscriberAddress}/creators/${creatorAddress}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many SubscriptionsCreators items from collection subscriptions/${subscriberAddress}/creators
 
  Read Operation Details: Public. Anyone can read subscription records to render follower counts, following lists, and check subscription state.
  
 */
export async function getManySubscriptionsCreators(subscriberAddress: string, filter: string = ""): Promise<SubscriptionsCreatorsResponse[]> {
  try {
    const results = await get(`subscriptions/${subscriberAddress}/creators`, { prompt: filter });
    return Array.isArray(results) ? results as SubscriptionsCreatorsResponse[] : [];
  } catch (error) {
    console.error(`Error getting SubscriptionsCreators collection at path 'subscriptions/${subscriberAddress}/creators': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in SubscriptionsCreators collection at subscriptions/${subscriberAddress}/creators
 
  Read Operation Details: Public. Anyone can read subscription records to render follower counts, following lists, and check subscription state.
  
 */
export function subscribeManySubscriptionsCreators(callback: (data: SubscriptionsCreatorsResponse[]) => void, subscriberAddress: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`subscriptions/${subscriberAddress}/creators`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as SubscriptionsCreatorsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to SubscriptionsCreators collection at path '${`subscriptions/${subscriberAddress}/creators`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all SubscriptionsCreators items from collection subscriptions/${subscriberAddress}/creators
 
  Read Operation Details: Public. Anyone can read subscription records to render follower counts, following lists, and check subscription state.
  
 */
export async function getAllSubscriptionsCreators(subscriberAddress: string, filter: string = ""): Promise<SubscriptionsCreatorsResponse[]> {
  try {
    const results = await get(`subscriptions/*/creators`, { prompt: filter });
    return Array.isArray(results) ? results as SubscriptionsCreatorsResponse[] : [];
  } catch (error) {
    console.error(`Error getting SubscriptionsCreators collection at path 'subscriptions/${subscriberAddress}/creators': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in SubscriptionsCreators collection at subscriptions/${subscriberAddress}/creators
 
  Read Operation Details: Public. Anyone can read subscription records to render follower counts, following lists, and check subscription state.
  
 */
export function subscribeAllSubscriptionsCreators(callback: (data: SubscriptionsCreatorsResponse[]) => void, subscriberAddress: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`subscriptions/*/creators`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as SubscriptionsCreatorsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to SubscriptionsCreators collection at path '${`subscriptions/*/creators`}': ${formatError(error)}`);
    }
  });
}

/**
 * Count SubscriptionsCreators items in collection subscriptions/${subscriberAddress}/creators.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countSubscriptionsCreators(subscriberAddress: string, filter: string = ""): Promise<AggregateResult> {
  try {
    return await count(`subscriptions/${subscriberAddress}/creators`, { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting SubscriptionsCreators at path 'subscriptions/${subscriberAddress}/creators': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on SubscriptionsCreators items in collection subscriptions/${subscriberAddress}/creators.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateSubscriptionsCreators(subscriberAddress: string, operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate(`subscriptions/${subscriberAddress}/creators`, operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating SubscriptionsCreators at path 'subscriptions/${subscriberAddress}/creators': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Two allowed paths: (1) SUBSCRIBER UNSUBSCRIBE — the subscriber (caller equals $subscriberAddress) deletes their own subscription record. (2) BACKEND TEARDOWN — PROJECT_VAULT_ADDRESS deletes subscription rows during the DELETE /api/profile creator-profile teardown flow. The backend must clean up BOTH directions: rows under subscriptions/{deletedUser}/creators/<USE_ASTERISK_HERE> (the deleted user's outgoing follows) AND rows under subscriptions/<USE_ASTERISK_HERE>/creators/{deletedUser} (incoming follows where the deleted user was the creator). Authorization via PROJECT_VAULT_ADDRESS lets the backend delete records authored by other subscribers when those follow the deleted user.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteSubscriptionsCreators(subscriberAddress: string, creatorAddress: string): Promise<boolean> {
  try {
    const succeeded = await set(`subscriptions/${subscriberAddress}/creators/${creatorAddress}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting SubscriptionsCreators: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for SubscriptionsCreators for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteSubscriptionsCreators(subscriberAddress: string, creatorAddress: string): DocumentOperation {
  return { path: `subscriptions/${subscriberAddress}/creators/${creatorAddress}`, document: null };
}

